import { ReactElement, useMemo } from 'react';
import { Radio, Typography } from '@mui/material';

import { useGetUnstakePeriodQuery } from 'modules/api/actions/getUnstakePeriod';
import { DECIMAL_PLACES_SHORT, THOUSAND, ZERO } from 'modules/common/const';
import { formatThousandAmount } from 'modules/common/utils/formatThousandAmount';
import { useGlobalTranslation } from 'modules/i18n';
import { useGetFlashUnstakeDataQuery } from 'modules/liquidStaking/actions/getFlashUnstakeData';
import { UnstakeType } from 'modules/liquidStaking/const';
import { useLiquidStakingPool } from 'modules/liquidStaking/hooks/useLiquidStakingPool';

import { translation } from './translation';
import { useStyles } from './useStyles';

interface IUnstakeTypeSelectProps {
  className?: string;
  type: UnstakeType;
  setType: (type: UnstakeType) => void;
}

export function UnstakeTypeSelect({
  className,
  type,
  setType,
}: IUnstakeTypeSelectProps): ReactElement {
  const { t, keys } = useGlobalTranslation(translation);
  const { classes, cx } = useStyles();

  const { poolAddress } = useLiquidStakingPool();
  const { data: days } = useGetUnstakePeriodQuery({
    poolAddress,
  });
  const { data: flashUnstakeData } = useGetFlashUnstakeDataQuery({
    poolAddress,
  });

  const upToAmountStr = useMemo(() => {
    if (!flashUnstakeData?.upToAmount) {
      return '';
    }

    const { upToAmount } = flashUnstakeData;

    if (upToAmount.isGreaterThanOrEqualTo(THOUSAND)) {
      return formatThousandAmount(upToAmount);
    }

    return upToAmount.decimalPlaces(0).toFormat();
  }, [flashUnstakeData]);

  return (
    <div className={cx(classes.root, className)}>
      <div
        className={classes.card}
        onClick={() => setType(UnstakeType.FlashUnstake)}
      >
        <div className={classes.type}>
          <Radio checked={type === UnstakeType.FlashUnstake} />

          <Typography variant="h5">{t(keys.instantUnstake)}</Typography>
        </div>

        {flashUnstakeData && (
          <div className={classes.details}>
            <Typography variant="body1">
              {t(keys.upTo, {
                value: upToAmountStr,
                token: t(keys.tokens.stakeToken),
              })}
            </Typography>

            <Typography variant="body1">
              {t(keys.fee, {
                value: flashUnstakeData.fee.decimalPlaces(DECIMAL_PLACES_SHORT),
              })}
            </Typography>
          </div>
        )}
      </div>

      <div
        className={classes.card}
        onClick={() => setType(UnstakeType.Unstake)}
      >
        <div className={classes.type}>
          <Radio checked={type === UnstakeType.Unstake} />

          <Typography variant="h5">{t(keys.standardUnstake)}</Typography>
        </div>

        <div className={classes.details}>
          {days && (
            <Typography variant="body1">
              {t(keys.period, { period: days.toFormat() })}
            </Typography>
          )}

          <Typography variant="body1">
            {t(keys.noFee, { value: ZERO })}
          </Typography>
        </div>
      </div>
    </div>
  );
}
